'use client';

import { AlertCircle, CheckCircle, LoaderCircle, Send } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { environment } from '@/config/environment';

type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};
type FormErrors = Partial<FormData>;

export function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Invalid email';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    else if (formData.message.trim().length < 10)
      newErrors.message = 'At least 10 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
    if (errors[field])
      setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        toast.success("Message sent! I'll get back to you soon.");
      } else throw new Error(result.error || 'Failed to send message');
    } catch (error) {
      console.error(error);
      toast.error('Error sending message. Try again or email directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted)
    return (
      <Card className="mx-auto max-w-lg">
        <CardContent className="p-8 text-center">
          <CheckCircle
            className="mx-auto mb-4 h-16 w-16 text-green-500"
            aria-hidden="true"
          />
          <h2 className="mb-2 text-3xl font-bold">Message Sent!</h2>
          <p className="text-muted-foreground mb-6">
            Thank you! I&apos;ll respond within 24 hours.
          </p>
          <Button onClick={() => setIsSubmitted(false)} variant="outline">
            Send Another
          </Button>
        </CardContent>
      </Card>
    );

  return (
    <Card className="mx-auto max-w-lg">
      <CardContent className="p-6">
        <h2 className="mb-6 text-center text-2xl font-semibold">
          Send me a message
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name, Email, Subject Fields */}
          {(['name', 'email', 'subject'] as (keyof FormData)[]).map((field) => (
            <div key={field} className="space-y-1">
              <label
                htmlFor={field}
                className="text-sm font-medium text-gray-700"
              >
                {field.charAt(0).toUpperCase() + field.slice(1)} *
              </label>
              <Input
                id={field}
                type={field === 'email' ? 'email' : 'text'}
                value={formData[field]}
                onChange={(event) => handleChange(field, event.target.value)}
                className={`focus:border-primary focus:ring-primary/20 w-full rounded-md border p-3 transition-colors focus:ring-1 focus:outline-none ${
                  errors[field]
                    ? 'border-destructive focus:border-destructive focus:ring-destructive/20'
                    : 'border-border'
                }`}
                disabled={isSubmitting}
                name={field}
              />
              {errors[field] && (
                <p className="text-destructive mt-1 flex items-center gap-1 text-sm">
                  <AlertCircle className="h-4 w-4" aria-hidden="true" />
                  {errors[field]}
                </p>
              )}
            </div>
          ))}

          {/* Message Field */}
          <div className="space-y-1">
            <label
              htmlFor="message"
              className="text-sm font-medium text-gray-700"
            >
              Message *
            </label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(event) => handleChange('message', event.target.value)}
              className={`focus:border-primary focus:ring-primary/20 min-h-28 w-full rounded-md border p-3 transition-colors focus:ring-1 focus:outline-none ${
                errors.message
                  ? 'border-destructive focus:border-destructive focus:ring-destructive/20'
                  : 'border-border'
              }`}
              disabled={isSubmitting}
              name="message"
            />
            {errors.message && (
              <p className="text-destructive mt-1 flex items-center gap-1 text-sm">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                {errors.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            size="lg"
            className="bg-primary text-primary-foreground hover:shadow-primary/20 focus:shadow-primary/20 w-full shadow-md transition-shadow hover:cursor-pointer hover:shadow-lg focus:shadow-lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2 h-5 w-5" /> Send Message
              </>
            )}
          </Button>
        </form>

        {/* Direct Contact Info */}
        <div className="border-border text-muted-foreground mt-6 border-t pt-4 text-center text-sm">
          <p>Or reach me directly at:</p>
          <a
            href={`mailto:${environment.NEXT_PUBLIC_CONTACT_EMAIL}`}
            className="text-primary font-medium hover:underline"
          >
            {environment.NEXT_PUBLIC_CONTACT_EMAIL}
          </a>
        </div>
      </CardContent>
    </Card>
  );
}
